import { createAsyncThunk, createEntityAdapter, createSelector, createSlice } from '@reduxjs/toolkit'
import { staffAssignmentAPI } from 'api/temple'
import { apiStatus } from 'shared/constants/AppEnum'
import { AppState } from 'store/reduxStore'
import { IApiState } from 'store/types'
import {
    StaffPujaAssignmentPayloadType,
    StaffPujaAssignmentResponseType,
    TempleIdType,
} from 'types/api/temple'
import { selectCurrentTempleId } from './templeSlice'

const staffAssignmentAdapter = createEntityAdapter<StaffPujaAssignmentResponseType>({
    sortComparer: (a, b) => b.startDate.localeCompare(a.startDate),
})

type InitialStateType = {
    api: IApiState
}

const initialState: InitialStateType = {
    api: {
        status: apiStatus.IDLE,
        error: null,
    },
}

export const staffAssignmentListThunk = createAsyncThunk(
    'staffAssignment/list',
    async (payload: TempleIdType = {}, { rejectWithValue, getState }) => {
        const templeId = payload.templeId ?? selectCurrentTempleId(getState() as AppState)
        try {
            const response = await staffAssignmentAPI.list(templeId)
            return response.data.map(assignment => ({ ...assignment, templeId }))
        } catch (err: any) {
            return rejectWithValue(err.response.data)
        }
    }
)

export const staffAssignmentRetrieveThunk = createAsyncThunk(
    'staffAssignment/retrieve',
    async (payload: IdType & TempleIdType, { rejectWithValue, getState }) => {
        const templeId = payload.templeId ?? selectCurrentTempleId(getState() as AppState)
        try {
            const response = await staffAssignmentAPI.get(payload.id, templeId)
            return { ...response.data, templeId }
        } catch (err: any) {
            return rejectWithValue(err.response.data)
        }
    }
)

export const staffAssignmentCreateThunk = createAsyncThunk(
    'staffAssignment/create',
    async (payload: StaffPujaAssignmentPayloadType & TempleIdType, { rejectWithValue, getState }) => {
        const templeId = payload.templeId ?? selectCurrentTempleId(getState() as AppState)
        try {
            const response = await staffAssignmentAPI.post(payload, templeId)
            return { ...response.data, templeId }
        } catch (err: any) {
            return rejectWithValue(err.response.data)
        }
    }
)

export const staffAssignmentUpdateThunk = createAsyncThunk(
    'staffAssignment/update',
    async (payload: IdType & TempleIdType & { data: Partial<StaffPujaAssignmentPayloadType> }, { rejectWithValue, getState }) => {
        const templeId = payload.templeId ?? selectCurrentTempleId(getState() as AppState)
        try {
            const response = await staffAssignmentAPI.update(payload.id, payload.data, templeId)
            return { ...response.data, templeId }
        } catch (err: any) {
            return rejectWithValue(err.response.data)
        }
    }
)

export const staffAssignmentDeleteThunk = createAsyncThunk(
    'staffAssignment/delete',
    async (payload: IdType & TempleIdType, { rejectWithValue, getState }) => {
        const templeId = payload.templeId ?? selectCurrentTempleId(getState() as AppState)
        try {
            await staffAssignmentAPI.delete(payload.id, templeId)
            return payload.id
        } catch (err: any) {
            return rejectWithValue(err.response.data)
        }
    }
)

export const staffAssignmentGenerateOccurrencesThunk = createAsyncThunk(
    'staffAssignment/generateOccurrences',
    async (payload: IdType & TempleIdType, { rejectWithValue, getState }) => {
        const templeId = payload.templeId ?? selectCurrentTempleId(getState() as AppState)
        try {
            const response = await staffAssignmentAPI.generateOccurrences(payload.id, templeId)
            return response.data
        } catch (err: any) {
            return rejectWithValue(err.response.data)
        }
    }
)

export const staffAssignmentReassignOccurrenceThunk = createAsyncThunk(
    'staffAssignment/reassignOccurrence',
    async (payload: TempleIdType & { occurrenceId: string | number, staffProfile: string | number }, { rejectWithValue, getState }) => {
        const templeId = payload.templeId ?? selectCurrentTempleId(getState() as AppState)
        try {
            const response = await staffAssignmentAPI.reassignOccurrence(payload.occurrenceId, payload.staffProfile, templeId)
            return response.data
        } catch (err: any) {
            return rejectWithValue(err.response.data)
        }
    }
)

const staffAssignmentSlice = createSlice({
    name: 'staffAssignment',
    initialState: staffAssignmentAdapter.getInitialState(initialState),
    reducers: {
        resetState(state) {
            state.api = {
                status: apiStatus.IDLE,
                error: null,
            }
            staffAssignmentAdapter.removeAll(state)
        },
    },
    extraReducers: builder => {
        const pending = (state: any) => {
            state.api = { status: apiStatus.LOADING, error: null }
        }
        const rejected = (state: any, action: any) => {
            state.api = { status: apiStatus.FAILED, error: action.payload }
        }

        builder.addCase(staffAssignmentListThunk.pending, pending)
        builder.addCase(staffAssignmentListThunk.fulfilled, (state, action) => {
            state.api = { status: apiStatus.SUCCEEDED, error: null }
            staffAssignmentAdapter.upsertMany(state, action.payload)
        })
        builder.addCase(staffAssignmentListThunk.rejected, rejected)
        builder.addCase(staffAssignmentRetrieveThunk.pending, pending)
        builder.addCase(staffAssignmentRetrieveThunk.fulfilled, (state, action) => {
            state.api = { status: apiStatus.SUCCEEDED, error: null }
            staffAssignmentAdapter.upsertOne(state, action.payload)
        })
        builder.addCase(staffAssignmentRetrieveThunk.rejected, rejected)
        builder.addCase(staffAssignmentCreateThunk.pending, pending)
        builder.addCase(staffAssignmentCreateThunk.fulfilled, (state, action) => {
            state.api = { status: apiStatus.SUCCEEDED, error: null }
            staffAssignmentAdapter.upsertOne(state, action.payload)
        })
        builder.addCase(staffAssignmentCreateThunk.rejected, rejected)
        builder.addCase(staffAssignmentUpdateThunk.pending, pending)
        builder.addCase(staffAssignmentUpdateThunk.fulfilled, (state, action) => {
            state.api = { status: apiStatus.SUCCEEDED, error: null }
            staffAssignmentAdapter.upsertOne(state, action.payload)
        })
        builder.addCase(staffAssignmentUpdateThunk.rejected, rejected)
        builder.addCase(staffAssignmentDeleteThunk.pending, pending)
        builder.addCase(staffAssignmentDeleteThunk.fulfilled, (state, action) => {
            state.api = { status: apiStatus.SUCCEEDED, error: null }
            staffAssignmentAdapter.removeOne(state, action.payload)
        })
        builder.addCase(staffAssignmentDeleteThunk.rejected, rejected)
        builder.addCase(staffAssignmentGenerateOccurrencesThunk.pending, pending)
        builder.addCase(staffAssignmentGenerateOccurrencesThunk.fulfilled, state => {
            state.api = { status: apiStatus.SUCCEEDED, error: null }
        })
        builder.addCase(staffAssignmentGenerateOccurrencesThunk.rejected, rejected)
        builder.addCase(staffAssignmentReassignOccurrenceThunk.pending, pending)
        builder.addCase(staffAssignmentReassignOccurrenceThunk.fulfilled, state => {
            state.api = { status: apiStatus.SUCCEEDED, error: null }
        })
        builder.addCase(staffAssignmentReassignOccurrenceThunk.rejected, rejected)
    },
})

export default staffAssignmentSlice.reducer

export const { resetState } = staffAssignmentSlice.actions

export const {
    selectAll: selectAllStaffAssignments,
    selectById: selectStaffAssignmentById,
} = staffAssignmentAdapter.getSelectors((state: AppState) => state.staffAssignment)

export const selectStaffAssignmentStatus = (state: AppState): IApiState => state.staffAssignment.api

export const selectCurrentTempleStaffAssignments = createSelector(
    [selectAllStaffAssignments, selectCurrentTempleId],
    (assignments, currentTemple) => assignments.filter(item => item.templeId === currentTemple)
)
